import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useFrequentPassengersContext from '../context/FrequentPassengers';
import useLoyaltyPrograms from '../loyalty/context/useLoyaltyPrograms';

/**
 * Custom hook to filter frequent passengers excluding the Doters passenger
 * when logged in and not the first passenger, and excluding all passengers
 * when in an exchange process
 * @param {number} passengerIndex - The current passenger index
 * @returns {Array} Filtered array of frequent passengers
 */
const useFilteredFrequentPassengers = (passengerIndex) => {
  const { frequentPassengers } = useFrequentPassengersContext();
  const { doters } = useLoyaltyPrograms();
  const isExchange = useSelector((state) => state.purchase.get('isExchange'));

  const filteredFrequentPassengers = useMemo(() => {
    // Handle null/undefined frequentPassengers
    if (!frequentPassengers) {
      return frequentPassengers;
    }

    // Convert passengerIndex to number for comparison
    const numericPassengerIndex = Number(passengerIndex);

    // If we're in an exchange process, don't allow any frequent passenger selection
    // This ensures the ticket remains tied to the original passenger who bought it
    if (isExchange) {
      return [];
    }

    if (!doters.userIsLoggedIn || numericPassengerIndex === 1) {
      return frequentPassengers;
    }

    // Find the passenger that is selected for the first position (Doters passenger)
    const dotersPassenger = frequentPassengers.find((fp) => fp.selectedForPassenger === 1);

    if (!dotersPassenger) {
      return frequentPassengers;
    }

    // Filter out the Doters passenger from the list
    return frequentPassengers.filter((fp) => fp.id !== dotersPassenger.id);
  }, [doters.userIsLoggedIn, passengerIndex, frequentPassengers, isExchange]);

  return filteredFrequentPassengers;
};

export default useFilteredFrequentPassengers;
